import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Settings, Target, ArrowDownToLine, Info, RotateCcw } from 'lucide-react';
import { DEFAULT_FOS_TARGETS } from '@/lib/calculations';

export const SettingsPanel = ({ 
  fosTargets, 
  setFosTargets,
  endBearingConfig,
  setEndBearingConfig,
  onResetFOS
}) => {
  // Update FOS target
  const updateFOSTarget = (key, value) => {
    setFosTargets(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  // Update end bearing config
  const updateEndBearing = (key, value) => {
    setEndBearingConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Update end bearing params
  const updateEndBearingParams = (key, value) => {
    setEndBearingConfig(prev => ({
      ...prev,
      params: {
        ...prev.params,
        [key]: parseFloat(value) || 0
      }
    }));
  };

  return (
    <div className="space-y-6" data-testid="settings-panel">
      {/* FOS Targets Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5 text-emerald-600" />
            Factor of Safety Targets
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>
                    Customize the target FOS values for each load case. 
                    These values determine PASS/FAIL status in the FOS check.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Operating Compression */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Operating Compression</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">FS ≥</span>
                <Input
                  type="number"
                  step="0.1"
                  value={fosTargets.compOperating}
                  onChange={(e) => updateFOSTarget('compOperating', e.target.value)}
                  className="w-20 engineering-input"
                  data-testid="fos-target-comp-operating"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Default: {DEFAULT_FOS_TARGETS.compOperating.toFixed(1)}
              </p>
            </div>

            {/* Storm Compression */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Storm Compression</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">FS ≥</span>
                <Input
                  type="number"
                  step="0.1"
                  value={fosTargets.compStorm}
                  onChange={(e) => updateFOSTarget('compStorm', e.target.value)}
                  className="w-20 engineering-input"
                  data-testid="fos-target-comp-storm"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Default: {DEFAULT_FOS_TARGETS.compStorm.toFixed(1)}
              </p>
            </div>

            {/* Operating Tension */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Operating Tension</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">FS ≥</span>
                <Input
                  type="number"
                  step="0.1"
                  value={fosTargets.tensOperating}
                  onChange={(e) => updateFOSTarget('tensOperating', e.target.value)}
                  className="w-20 engineering-input"
                  data-testid="fos-target-tens-operating"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Default: {DEFAULT_FOS_TARGETS.tensOperating.toFixed(1)}
              </p>
            </div>

            {/* Storm Tension */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Storm Tension</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm">FS ≥</span>
                <Input
                  type="number"
                  step="0.1"
                  value={fosTargets.tensStorm}
                  onChange={(e) => updateFOSTarget('tensStorm', e.target.value)}
                  className="w-20 engineering-input"
                  data-testid="fos-target-tens-storm"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Default: {DEFAULT_FOS_TARGETS.tensStorm.toFixed(1)}
              </p>
            </div>
          </div>

          {/* Reset Button */}
          <div className="mt-4 pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={onResetFOS}
              data-testid="reset-fos-targets-btn"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* End Bearing Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ArrowDownToLine className="h-5 w-5 text-blue-600" />
            End Bearing Capacity
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>
                    Enable end bearing to add tip resistance to the pile capacity calculation.
                    Choose between Abbs & Needham or API RP 2GEO methods.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Enable Toggle */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <Label className="font-medium">Enable End Bearing</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Include tip resistance in capacity calculation
              </p>
            </div>
            <Switch
              checked={endBearingConfig.enabled}
              onCheckedChange={(checked) => updateEndBearing('enabled', checked)}
              data-testid="end-bearing-toggle"
            />
          </div>

          {/* End Bearing Configuration */}
          {endBearingConfig.enabled && (
            <div className="space-y-6 pt-4 border-t">
              {/* Method Selection */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Calculation Method</Label>
                <RadioGroup
                  value={endBearingConfig.method}
                  onValueChange={(value) => updateEndBearing('method', value)}
                  className="space-y-3"
                >
                  {/* Abbs & Needham */}
                  <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                    <RadioGroupItem value="abbsNeedham" id="eb-abbs" className="mt-1" data-testid="eb-method-abbs" />
                    <div className="flex-1">
                      <Label htmlFor="eb-abbs" className="font-medium cursor-pointer">
                        Abbs & Needham (Rock)
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        q<sub>b</sub> = 2.5 × q<sub>u</sub> (capped at 5 MPa). Uses rock UCS at pile tip.
                      </p>
                    </div>
                  </div>

                  {/* API RP 2GEO */}
                  <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                    <RadioGroupItem value="api" id="eb-api" className="mt-1" data-testid="eb-method-api" />
                    <div className="flex-1">
                      <Label htmlFor="eb-api" className="font-medium cursor-pointer">
                        API RP 2GEO
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Industry standard method for clay, sand, or rock.
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* API Parameters (shown when API method selected) */}
              {endBearingConfig.method === 'api' && (
                <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                  <Label className="text-sm font-medium">API RP 2GEO Parameters</Label>
                  
                  {/* Soil Type */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Soil Type at Pile Tip</Label>
                    <Select
                      value={endBearingConfig.params.soilType || 'rock'}
                      onValueChange={(value) => updateEndBearingParams('soilType', value)}
                    >
                      <SelectTrigger className="w-full" data-testid="eb-soil-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clay">Clay</SelectItem>
                        <SelectItem value="sand">Sand</SelectItem>
                        <SelectItem value="rock">Weak Rock</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Clay Parameters */}
                  {endBearingConfig.params.soilType === 'clay' && (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">
                        Undrained Shear Strength s<sub>u</sub> (kPa)
                      </Label>
                      <Input
                        type="number"
                        value={endBearingConfig.params.su || 100}
                        onChange={(e) => updateEndBearingParams('su', e.target.value)}
                        className="engineering-input"
                        data-testid="eb-su"
                      />
                      <p className="text-xs text-muted-foreground">
                        q<sub>b</sub> = 9 × s<sub>u</sub>
                      </p>
                    </div>
                  )}

                  {/* Sand Parameters */}
                  {endBearingConfig.params.soilType === 'sand' && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">
                          Friction Angle φ (degrees)
                        </Label>
                        <Input
                          type="number"
                          value={endBearingConfig.params.frictionAngle || 30}
                          onChange={(e) => updateEndBearingParams('frictionAngle', e.target.value)}
                          className="engineering-input"
                          data-testid="eb-friction-angle"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">
                          Effective Stress σ'<sub>v</sub> (kPa)
                        </Label>
                        <Input
                          type="number"
                          value={endBearingConfig.params.effectiveStress || 200}
                          onChange={(e) => updateEndBearingParams('effectiveStress', e.target.value)}
                          className="engineering-input"
                          data-testid="eb-effective-stress"
                        />
                        <p className="text-xs text-muted-foreground">
                          q<sub>b</sub> = N<sub>q</sub> × σ'<sub>v</sub>
                        </p>
                      </div>
                    </>
                  )}

                  {/* Rock Parameters - uses qu from soil layers */}
                  {endBearingConfig.params.soilType === 'rock' && (
                    <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                      <p className="text-sm text-blue-700 dark:text-blue-400">
                        Rock end bearing uses q<sub>u</sub> from the deepest soil layer at the pile tip.
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        q<sub>b</sub> = 2.5 × q<sub>u</sub> (capped at 10 MPa)
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Preview (if results available) */}
              <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400 mb-2">
                  <ArrowDownToLine className="h-4 w-4" />
                  <span className="text-sm font-medium">End Bearing Configuration Active</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Run analysis to see updated capacity including end bearing.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPanel;
